import { ROUTE_BUILDER } from '@/constants/pages';
import { DEFAULT_NAVBAR_HEIGHT } from '@/constants/styleConstants';
import useViewToggle from '@/features/searchV2/components/ViewToggle/ViewToggle';
import { trackSearchPerformed } from '@/features/searchV2/helpers/tracking';
import { SearchContext as TSearchContext } from '@/features/searchV2/types/tracking';
import { locationSlug } from '@/helpers/urlTs';
import { useAppSelector } from '@/hooks';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
  SEARCH_PAGE_VIEWS,
} from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug, SearchType } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput, PerformSearchOptions } from '@/hooks/search/useSearch.types';
import { CoreSearchState, FetchArgs, useSearchManagerCore } from '@/hooks/search/useSearchManagerCore';
import { useSearchParams } from '@/hooks/search/useSearchParams';
import useInitialLoading from '@/hooks/useInitialLoading';
import { DEALS_SEARCH_ACTION, DealsSearchState } from '@/reducers/dealsSearchReducer';
import { AppDispatch } from '@/store';
import { UrlSearchState } from '@/types/state/search';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getSearchPerformedTrackingProps } from '../tracking';
import { buildUrlSearchQuery, requestAndSetDealsSearchResults } from './useDealsSearch.helpers';
import { consumeDealsSearchStateForCurrentUrl, saveDealsSearchStateToCache } from './useDealsSearchStateCache';
import { isRedirectedFromPreview, isSmtModeInSync, isSmtOnlySearchEnabled } from './useSmtOnlyToggle';

export type DealsSearchType = 'deals' | 'deals_preview' | 'deals_sistaminuten' | 'brand_deals';

export type DealsSearchManagerProps = {
  type: DealsSearchType;
};

type DealsSearchManagerOutput = BaseSearchManagerOutput & {
  initialLoading: boolean;
  extendSearchResults: () => void;
  navigateToPage: (page: number) => void;
  extendedFetching: boolean;
  isExtended: boolean;
  saveDealsSearchState: () => void;
  placeId?: number;
};

export const useDealsSearchManager = ({ type }: DealsSearchManagerProps) => {
  const searchState = useAppSelector((state) => state.dealsSearch);
  const searchParams = useSearchParams({});
  const routerLocation = useLocation();
  const history = useHistory();

  // Read page directly from the URL as 1-based. params.page (from getSearchUrlParameters) is
  // 0-based and has an inconsistent fallback, so we bypass it here for API calls and SEO titles.
  const urlPage = useMemo(() => {
    const p = new URLSearchParams(routerLocation.search).get('page');
    const n = p ? parseInt(p, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }, [routerLocation.search]);

  // Set when the user picks a merchant suggestion; scopes results to that merchant.
  const urlPlaceId = useMemo(() => {
    const n = Number(new URLSearchParams(routerLocation.search).get('placeId'));
    return Number.isInteger(n) && n > 0 ? n : undefined;
  }, [routerLocation.search]);

  const initialLoading = useInitialLoading(searchState.fetching, searchState.pendingSSRHydration);
  const [restoredFromCache, setRestoredFromCache] = useState(false);

  const viewToggle = useViewToggle({
    views: [...SEARCH_PAGE_VIEWS],
    initialState: SEARCH_PAGE_VIEWS[0],
    className: 'px-sm',
  });

  // Redux state adapter for core hook
  const useCoreSearchState = useCallback(
    (): CoreSearchState => ({
      bounds: searchState.bounds,
      isSSRFetched: searchState.pendingSSRHydration,
      searchContext: undefined,
      isIndexable: searchState.isIndexable,
    }),
    [searchState.bounds, searchState.pendingSSRHydration],
  );

  const redirectedFromPreview = isRedirectedFromPreview(routerLocation.state);

  // Build URL from merged params — handles new-search → preview or SMT SERP vs filter/sort → same route
  const buildUrl = useCallback(
    (mergedParams: UrlSearchState, currentParams: UrlSearchState, options?: PerformSearchOptions) => {
      const newQ = mergedParams.q ?? searchParams.q;
      const newLocation = mergedParams.location ?? searchParams.location;
      const isNewSearch = newQ !== currentParams.q || newLocation !== currentParams.location;
      const smtOnly = isSmtOnlySearchEnabled(type, redirectedFromPreview);
      const switchesSmtMode = !!options?.fromSearchForm && !isSmtModeInSync(smtOnly, type, redirectedFromPreview);

      // Clear lat/lon if location changed and not "near me" or "closest" sort
      if (
        newLocation !== currentParams.location &&
        locationSlug(newLocation) !== DEFAULT_MY_LOCATION_KEYWORD &&
        mergedParams.sort !== 'closest'
      ) {
        mergedParams.lat = undefined;
        mergedParams.lon = undefined;
      }

      const merged = mergedParams as unknown as UrlSearchState;

      if (isNewSearch || switchesSmtMode) {
        const what = (newQ || DEFAULT_SEARCH_KEYWORD).trim();
        const where = (newLocation || DEFAULT_SEARCH_LOCATION).trim();
        const indexableKeyword = getKeywordWithSlug(what);
        const indexableLocation = getLocationWithSlug(where);
        const newIsIndexable = !!(indexableKeyword?.slug && indexableLocation?.slug);
        const search = buildUrlSearchQuery(
          { ...merged, placeId: isNewSearch ? undefined : urlPlaceId },
          newIsIndexable,
        );
        let pathname: string;
        if (newIsIndexable) {
          pathname = smtOnly
            ? ROUTE_BUILDER.sistaMinutenSerp(indexableKeyword.slug, indexableLocation.slug)
            : ROUTE_BUILDER.dealsSerpPreview(indexableKeyword.slug, indexableLocation.slug);
        } else {
          pathname = smtOnly ? ROUTE_BUILDER.sistaMinutenSerpNoIndex() : ROUTE_BUILDER.dealsSerpPreviewNoIndex();
        }
        return { pathname, search };
      }

      const isIndexable = !!(
        getKeywordWithSlug(searchParams.q)?.slug && getLocationWithSlug(searchParams.location)?.slug
      );
      const search = buildUrlSearchQuery({ ...merged, placeId: urlPlaceId }, isIndexable);
      return { pathname: routerLocation.pathname, search };
    },
    [type, redirectedFromPreview, searchParams.q, searchParams.location, routerLocation.pathname, urlPlaceId],
  );

  // Fetch results — called by the core effect on URL changes
  const fetchResults = useCallback(
    async (args: FetchArgs, dispatch: AppDispatch) => {
      await requestAndSetDealsSearchResults(
        {
          type,
          params: { ...args.params, page: urlPage, placeId: urlPlaceId },
          state: searchState,
          resetAllSearchItems: !args.isLoadMore,
          bounds: args.bounds,
          searchContext: args.searchContext,
        },
        dispatch,
      );
    },
    [type, searchState, urlPage, urlPlaceId],
  );

  const didFetchResultsSSR = useCallback(() => {
    trackSearchPerformed(
      getSearchPerformedTrackingProps({
        type,
        state: searchState,
        urlState: params,
        context: searchState.searchContext,
        screenName: 'deals_search_results',
      }),
    );
  }, []);

  const initialScrollToItemIndex = (routerLocation.state as { scrollToItemIndex?: unknown } | null)?.scrollToItemIndex;
  const scrollToItemIndexRef = useRef<number | null>(
    typeof initialScrollToItemIndex === 'number' &&
      Number.isInteger(initialScrollToItemIndex) &&
      initialScrollToItemIndex >= 0
      ? initialScrollToItemIndex
      : null,
  );

  // DealsSearchProvider stays mounted across landing → SERP nav (single <Route>
  // matches both paths), so the useRef initializer only fires once on landing.
  // Re-read scrollToItemIndex whenever router state changes so a later "Visa alla"
  // click can deliver its index.
  useEffect(() => {
    const incoming = (routerLocation.state as { scrollToItemIndex?: unknown } | null)?.scrollToItemIndex;
    if (typeof incoming === 'number' && Number.isInteger(incoming) && incoming >= 0) {
      scrollToItemIndexRef.current = incoming;
    }
  }, [routerLocation.state]);

  useEffect(() => {
    if (scrollToItemIndexRef.current === null) return;
    if (!searchState.list) return;

    const targetIndex = scrollToItemIndexRef.current;
    scrollToItemIndexRef.current = null;

    const scrollToTarget = () => {
      const grid = document.querySelector('[data-deals-results-grid]');
      const targetEl = grid?.children[targetIndex] as HTMLElement | null;
      if (!targetEl) return;
      const headerEl = document.querySelector('header');
      const navHeight = headerEl?.getBoundingClientRect().height ?? DEFAULT_NAVBAR_HEIGHT;
      const elTop = targetEl.getBoundingClientRect().top + window.scrollY;
      const maxScrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo(0, Math.min(Math.max(0, elTop - navHeight), maxScrollable));
    };

    const rafId = requestAnimationFrame(scrollToTarget);
    return () => cancelAnimationFrame(rafId);
  }, [searchState.list]);

  /**
   * Refs used to restore SERP state when the user comes back from a deal/place
   * detail page (browser back button). On the first effect run after mount we
   * check the sessionStorage cache: if it matches the current URL we hydrate
   * Redux with the cached state and tell the core hook to skip the initial
   * fetch by returning the cached results from getPageCache.
   */
  const restoreAttemptedRef = useRef(false);
  const restoredPageRef = useRef<number | null>(null);
  const restoredScrollRef = useRef<number | null>(null);
  const dispatchRef = useRef<AppDispatch | null>(null);

  const getPageCache = useCallback((page: number) => {
    if (restoreAttemptedRef.current) return undefined;
    restoreAttemptedRef.current = true;

    const cached = consumeDealsSearchStateForCurrentUrl();
    if (!cached) return undefined;

    const cachedResults = cached.state.list?.results;
    if (!cachedResults || cachedResults.length === 0) return undefined;

    if (dispatchRef.current) {
      dispatchRef.current({
        type: DEALS_SEARCH_ACTION.SET_RESTORE_STATE,
        payload: cached.state,
      });
    }

    restoredPageRef.current = page;
    restoredScrollRef.current = cached.scrollPosition;
    setRestoredFromCache(true);

    // Return cached results so the core skips fetching for this page.
    return cachedResults as unknown as any[];
  }, []);

  /**
   * Core hook that should be reusable (refactor normal search when we have time)
   */
  const core = useSearchManagerCore({
    params: searchParams,
    fetchResults,
    didFetchResultsSSR,
    buildUrl,
    resetAction: DEALS_SEARCH_ACTION.SET_RESET,
    useSearchState: useCoreSearchState,
    consumeSSRAction: DEALS_SEARCH_ACTION.CONSUME_SSR_HYDRATION,
    debounceMs: 0,
    getPageCache,
  });

  dispatchRef.current = core.dispatch;

  // Restore scroll position once Redux has been hydrated with the cached state.
  // Retry until the document is tall enough (cards/images may still be laying out).
  useEffect(() => {
    if (restoredScrollRef.current === null) return;
    if (!searchState.list) return;
    const targetY = restoredScrollRef.current;
    restoredScrollRef.current = null;

    const MAX_ATTEMPTS = 60; // ~1s at 60fps
    let attempts = 0;
    let rafId = 0;

    const tryScroll = () => {
      const maxScrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const desired = Math.min(targetY, maxScrollable);
      window.scrollTo(0, desired);
      attempts += 1;
      if (desired < targetY && attempts < MAX_ATTEMPTS) {
        rafId = requestAnimationFrame(tryScroll);
      }
    };

    rafId = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(rafId);
  }, [searchState.list]);

  const { performSearch, paginate, params } = core;

  /**
   * Prevent race condition on double tap extend button
   */
  const loadMoreInFlightRef = useRef(false);

  const extendSearchResults = useCallback(async () => {
    if (loadMoreInFlightRef.current) return;
    if (!searchState.list || searchState.list.isLastPage) return;

    loadMoreInFlightRef.current = true;
    try {
      const nextPage = (searchState.list.page ?? 1) + 1;
      await requestAndSetDealsSearchResults(
        {
          type,
          params: { ...searchParams, page: nextPage, placeId: urlPlaceId },
          state: searchState,
          resetAllSearchItems: false,
          bounds: searchState.bounds,
        },
        core.dispatch,
      );
      const sp = new URLSearchParams(routerLocation.search);
      sp.set('page', String(nextPage));
      core.skipNextEffect();
      history.replace({
        pathname: routerLocation.pathname,
        search: sp.toString(),
        state: routerLocation.state,
      });
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [type, searchParams, searchState, core, history, routerLocation, urlPlaceId]);

  const navigateToPage = useCallback(
    async (page: number) => {
      await requestAndSetDealsSearchResults(
        {
          type,
          params: { ...searchParams, page, placeId: urlPlaceId },
          state: searchState,
          resetAllSearchItems: true,
          bounds: searchState.bounds,
        },
        core.dispatch,
      );
      const sp = new URLSearchParams(routerLocation.search);
      if (page === 1) {
        sp.delete('page');
      } else {
        sp.set('page', String(page));
      }
      core.skipNextEffect();
      history.push({
        pathname: routerLocation.pathname,
        search: sp.toString(),
        state: routerLocation.state,
      });
    },
    [type, searchParams, searchState, core, history, routerLocation, urlPlaceId],
  );

  useEffect(() => {
    if (searchState.fetching) {
      window.scrollTo(0, 0);
    }
  }, [searchState.fetching]);

  const brandName =
    type === 'brand_deals' && searchState.list?.type === 'brand_deals' ? searchState.list.template?.name : undefined;

  /**
   * Snapshot the current SERP state to sessionStorage so it can be restored if
   * the user navigates back to this page via the browser back button (e.g.
   * from a deal/place detail page).
   */
  const searchStateRef = useRef<DealsSearchState>(searchState);
  searchStateRef.current = searchState;
  const saveDealsSearchState = useCallback(() => {
    saveDealsSearchStateToCache(searchStateRef.current);
  }, []);

  return {
    q: brandName ?? params.q,
    location: params.location,
    startDate: params.startDate,
    endDate: params.endDate,
    timeOfDay: params.timeOfDay,
    prefs: params.prefs,
    sort: params.sort,
    lat: params.lat,
    lon: params.lon,
    page: urlPage,
    fetching: searchState.fetching,
    searchContext: undefined as TSearchContext | undefined,
    type: type as SearchType,
    viewToggle,
    isIndexable: searchState.isIndexable,
    initialLoading: restoredFromCache ? false : initialLoading,
    extendedFetching: searchState.extendedFetching,
    isExtended: searchState.isExtended,
    performSearch,
    paginate,
    extendSearchResults,
    navigateToPage,
    saveDealsSearchState,
    placeId: urlPlaceId,
  } satisfies DealsSearchManagerOutput;
};

export const DealsSearchContext = createContext<DealsSearchManagerOutput>({
  type: 'deals',
  q: '',
  location: '',
  startDate: '',
  endDate: '',
  timeOfDay: undefined,
  fetching: false,
  page: 1,
  prefs: undefined,
  sort: undefined,
  lat: undefined,
  lon: undefined,
  searchContext: undefined,
  performSearch: () => {},
  paginate: () => ({ nextPage: () => {}, prevPage: () => {} }),
  viewToggle: {
    view: 'list',
    setView: () => {},
    render: null,
  },
  isIndexable: true,
  initialLoading: false,
  extendSearchResults: () => {},
  navigateToPage: () => {},
  extendedFetching: false,
  isExtended: false,
  saveDealsSearchState: () => {},
});

export const useDealsSearchContext = (): DealsSearchManagerOutput => {
  const context = useContext(DealsSearchContext);
  if (!context) {
    throw new Error('useDealsSearchContext must be used within a DealsSearchProvider');
  }
  return context;
};

export const DealsSearchProvider: React.FC<{
  type: DealsSearchManagerProps['type'];
  children: React.ReactNode;
}> = ({ type, children }) => {
  const manager = useDealsSearchManager({ type });
  return <DealsSearchContext.Provider value={manager}>{children}</DealsSearchContext.Provider>;
};
